import { create } from "zustand";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";
import {
  AgentRule,
  ListAgentRulesResponseSchema,
  CreateAgentRuleResponseSchema,
  UpdateAgentRuleResponseSchema,
} from "@forgepoint/shared";

interface RuleLoadingState {
  isLoadingGet: boolean;
  isLoadingSave: boolean;
  isLoadingArchive: boolean;
  getError: string | null;
  saveError: string | null;
  archiveError: string | null;
}

interface AgentRulesStore {
  rules: Map<string, AgentRule>;
  ruleLoadingStates: Map<string, RuleLoadingState>;
  listLoading: boolean;
  listError: string | null;

  getRule: (id: string) => AgentRule | undefined;
  getRuleLoadingState: (id: string) => RuleLoadingState;
  fetchRules: (search?: string, includeArchived?: boolean) => Promise<string[]>;
  createRule: (
    content: string,
    actorId: string,
    actorType: string,
  ) => Promise<AgentRule | null>;
  updateRule: (ruleId: string, content: string) => Promise<AgentRule | null>;
  setRuleArchived: (ruleId: string, archived: boolean) => Promise<boolean>;
}

const getInitialLoadingState = (): RuleLoadingState => ({
  isLoadingGet: false,
  isLoadingSave: false,
  isLoadingArchive: false,
  getError: null,
  saveError: null,
  archiveError: null,
});

export const useAgentRulesStore = create<AgentRulesStore>((set, get) => ({
  rules: new Map(),
  ruleLoadingStates: new Map(),
  listLoading: false,
  listError: null,

  getRule: (id: string) => get().rules.get(id),

  getRuleLoadingState: (id: string) =>
    get().ruleLoadingStates.get(id) || getInitialLoadingState(),

  fetchRules: async (search?: string, includeArchived = false) => {
    set(() => ({ listLoading: true, listError: null }));

    try {
      const params = new URLSearchParams();
      if (search) {
        params.set("search", search);
      }
      if (includeArchived) {
        params.set("includeArchived", "true");
      }
      const query = params.toString();
      const url = query ? `agent-rules?${query}` : "agent-rules";
      const response = await fetchApi(url, "GET");

      if (response.ok && response.data) {
        const data = ListAgentRulesResponseSchema.parse(response.data);
        set((state) => {
          const rulesMap = new Map(state.rules);
          data.rules.forEach((rule) => {
            rulesMap.set(rule.id, rule);
          });
          return { rules: rulesMap, listLoading: false };
        });
        return data.rules.map((rule) => rule.id);
      }

      set(() => ({ listError: "Failed to fetch rules", listLoading: false }));
      return [];
    } catch (error) {
      captureException(error as Error);
      set(() => ({ listError: "Failed to fetch rules", listLoading: false }));
      return [];
    }
  },

  createRule: async (content: string, actorId: string, actorType: string) => {
    try {
      const response = await fetchApi("agent-rules", "POST", {
        body: { content, actorId, actorType },
      });

      if (response.ok && response.data) {
        const result = CreateAgentRuleResponseSchema.parse(response.data);
        const rulesMap = get().rules;
        rulesMap.set(result.rule.id, result.rule);
        set({ rules: new Map(rulesMap) });
        return result.rule;
      }

      return null;
    } catch (error) {
      captureException(error as Error);
      return null;
    }
  },

  updateRule: async (ruleId: string, content: string) => {
    set((state) => {
      const loadingStates = new Map(state.ruleLoadingStates);
      const currentState =
        loadingStates.get(ruleId) || getInitialLoadingState();
      loadingStates.set(ruleId, {
        ...currentState,
        isLoadingSave: true,
        saveError: null,
      });
      return { ruleLoadingStates: loadingStates };
    });

    try {
      const response = await fetchApi(`agent-rules/${ruleId}`, "PATCH", {
        body: { content },
      });

      if (response.ok && response.data) {
        const result = UpdateAgentRuleResponseSchema.parse(response.data);
        set((state) => {
          const rulesMap = new Map(state.rules);
          const loadingStates = new Map(state.ruleLoadingStates);
          const currentState =
            loadingStates.get(ruleId) || getInitialLoadingState();

          rulesMap.set(ruleId, result.rule);
          loadingStates.set(ruleId, {
            ...currentState,
            isLoadingSave: false,
            saveError: null,
          });
          return {
            rules: rulesMap,
            ruleLoadingStates: loadingStates,
          };
        });
        return result.rule;
      }

      set((state) => {
        const loadingStates = new Map(state.ruleLoadingStates);
        const currentState =
          loadingStates.get(ruleId) || getInitialLoadingState();
        loadingStates.set(ruleId, {
          ...currentState,
          isLoadingSave: false,
          saveError: "Failed to update rule",
        });
        return { ruleLoadingStates: loadingStates };
      });
      return null;
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const loadingStates = new Map(state.ruleLoadingStates);
        const currentState =
          loadingStates.get(ruleId) || getInitialLoadingState();
        loadingStates.set(ruleId, {
          ...currentState,
          isLoadingSave: false,
          saveError: "Failed to update rule",
        });
        return { ruleLoadingStates: loadingStates };
      });
      return null;
    }
  },

  setRuleArchived: async (ruleId: string, archived: boolean) => {
    set((state) => {
      const loadingStates = new Map(state.ruleLoadingStates);
      const currentState =
        loadingStates.get(ruleId) || getInitialLoadingState();
      loadingStates.set(ruleId, {
        ...currentState,
        isLoadingArchive: true,
        archiveError: null,
      });
      return { ruleLoadingStates: loadingStates };
    });

    try {
      const action = archived ? "archive" : "unarchive";
      const response = await fetchApi(
        `agent-rules/${ruleId}/${action}`,
        "POST",
      );

      if (response.ok && response.data) {
        const result = UpdateAgentRuleResponseSchema.parse(response.data);
        set((state) => {
          const rulesMap = new Map(state.rules);
          const loadingStates = new Map(state.ruleLoadingStates);
          const currentState =
            loadingStates.get(ruleId) || getInitialLoadingState();
          rulesMap.set(ruleId, result.rule);
          loadingStates.set(ruleId, {
            ...currentState,
            isLoadingArchive: false,
            archiveError: null,
          });
          return {
            rules: rulesMap,
            ruleLoadingStates: loadingStates,
          };
        });
        return true;
      }

      const errorMessage =
        (response.data as { error?: string })?.error ||
        `Failed to ${action} rule`;
      set((state) => {
        const loadingStates = new Map(state.ruleLoadingStates);
        const currentState =
          loadingStates.get(ruleId) || getInitialLoadingState();
        loadingStates.set(ruleId, {
          ...currentState,
          isLoadingArchive: false,
          archiveError: errorMessage,
        });
        return { ruleLoadingStates: loadingStates };
      });
      return false;
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const loadingStates = new Map(state.ruleLoadingStates);
        const currentState =
          loadingStates.get(ruleId) || getInitialLoadingState();
        loadingStates.set(ruleId, {
          ...currentState,
          isLoadingArchive: false,
          archiveError: `Failed to ${archived ? "archive" : "unarchive"} rule`,
        });
        return { ruleLoadingStates: loadingStates };
      });
      return false;
    }
  },
}));
